import { Actions, createEffect, ofType } from '@ngrx/effects';
import { auth2Actions } from '@core/auth2/store/auth2.actions';
import { inject } from '@angular/core';
import { UserTenantService } from '@core/user-tenant/user-tenant.service';
import { fromEvent, merge, of, switchMap } from 'rxjs';
import { userTenantApiActions } from '@core/user-tenant/user-tenant-api.actions';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { userTenantActions } from '@core/user-tenant/user-tenant.actions';
import { Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { rememberTenantId } from '@core/user-tenant/tenant-context';

export const listUserTenants = createEffect((
  actions$ = inject(Actions),
  userTenantService = inject(UserTenantService),
) => {
  return actions$.pipe(
    ofType(auth2Actions.checkAuthenticationStatusSuccess),
    switchMap(action => userTenantService.listUserTenants$().pipe(
      map(userTenants => userTenantApiActions.listSuccess({
        homeTenantId: action.currentUser.homeTenantId,
        userTenants,
      })),
      catchError(err => of(userTenantApiActions.listFailure({ err }))),
    )),
  );
}, { functional: true });

/**
 * Extracted so specs can substitute a spy for the navigation without jsdom
 * having to perform a real page load. Not exported: `provideEffects` treats
 * every export of this module as a functional effect, so a plain helper
 * function here would break that typing.
 */
const reloadFromOrigin = (): void => {
  window.location.href = window.location.origin;
};

export const saveSelectedTenantToSessionStorage = createEffect((
  actions$ = inject(Actions),
  reload = reloadFromOrigin,
) => {
  return actions$.pipe(
    ofType(userTenantActions.selectTenant),
    tap(({ tenantId }) => {
      rememberTenantId(tenantId);
      reload();
    }),
  );
}, { dispatch: false, functional: true });

/**
 * Whenever the tenant list resolves (and with it, the selected tenant -
 * see the reducer), persist the resolved tenant so a fresh tab opened in
 * the same session bootstraps synchronously into the same tenant.
 */
export const rememberResolvedTenant = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {
  return actions$.pipe(
    ofType(userTenantApiActions.listSuccess),
    concatLatestFrom(() => store.select(userTenantFeature.selectSelectedTenantId)),
    tap(([, selectedTenantId]) => {
      if (selectedTenantId)
        rememberTenantId(selectedTenantId);
    }),
  );
}, { dispatch: false, functional: true });

/**
 * Keep the cross-tab "last selected tenant" in sync with whichever tab was
 * most recently active, so a newly opened tab inherits the tenant of the
 * tab the user was last looking at rather than a stale value.
 */
export const trackActiveTabTenant = createEffect((
  store = inject(Store),
) => {
  const selectedTenantId$ = merge(
    fromEvent(window, 'focus'),
    fromEvent(document, 'visibilitychange').pipe(
      filter(() => document.visibilityState === 'visible'),
    ),
  ).pipe(
    concatLatestFrom(() => store.select(userTenantFeature.selectSelectedTenantId)),
    map(([, selectedTenantId]) => selectedTenantId),
    filter((tenantId): tenantId is string => Boolean(tenantId)),
  );

  return selectedTenantId$.pipe(
    // Explicit annotation works around a TS inference quirk where the
    // `createEffect(..., { functional: true, dispatch: false })` overload's
    // `() => Observable<unknown>` constraint leaks into this callback's
    // inferred parameter type despite `selectedTenantId$` already being a
    // narrowed `Observable<string>`.
    tap((tenantId: string) => rememberTenantId(tenantId)),
  );
}, { dispatch: false, functional: true });
